import { useState, useMemo } from "react";
import { Modal, Input, Table, Badge, Button, Card, Row, Col, Tooltip, Select, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder, faFile, faChevronLeft, faChevronRight, faEye, faFilter } from "@fortawesome/free-solid-svg-icons";
import { ExcelExportButton } from "./exportToExcel";
const { Text } = Typography;
const { Option } = Select;

export function FolderFilesModal({ isOpen, onClose, projectName, folderName, files, permissionExport }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const filesPerPage = 10;

  const baseUrl = "https://bodecom.com";
  const baseDir = `${baseUrl}/DocumentosFAO/`;

  const fetchFileUrl = (path) => {
    const fileUrl = `${baseDir}/${path}`;
    window.open(fileUrl, '_blank');
  };

  const getProjectName = (fullPath) => {
    if (!fullPath) return 'No especificado';
    const pathParts = fullPath.split('/');
    if (pathParts.length >= 6) {
      return pathParts[5];
    }
    if (pathParts.length > 0) {
      const projectPart = pathParts.find(part => part.match(/^P\d+/));
      if (projectPart) {
        return projectPart;
      }

      const nonGenericParts = pathParts.filter(part =>
        !part.includes('actividad_') &&
        !part.includes('tarea_') &&
        !part.includes('indicador_') &&
        !part.includes('medio_verificacion_') &&
        part !== 'DocumentosPOA'
      );
      if (nonGenericParts.length > 0) {
        return nonGenericParts[0];
      }
    }
    return 'No especificado';
  };

  // Función para obtener el estado del archivo
  const getFileStatus = (file) => {
    if (file.validado === 2) return "validated";
    if (file.validado === 1) return "notValidated";
    return "notRegistered";
  };

  const filteredFiles = useMemo(
    () => {
      let filtered = files.filter(
        (file) =>
          file.nameResource.toLowerCase().includes(searchTerm.toLowerCase()) ||
          file.fullPath.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getProjectName(file.fullPath)?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (statusFilter !== "all") {
        filtered = filtered.filter(file => getFileStatus(file) === statusFilter);
      }

      return filtered;
    },
    [files, searchTerm, statusFilter]
  );

  const paginatedFiles = useMemo(() => {
    const startIndex = (currentPage - 1) * filesPerPage;
    return filteredFiles.slice(startIndex, startIndex + filesPerPage);
  }, [filteredFiles, currentPage]);

  const totalPages = Math.ceil(filteredFiles.length / filesPerPage);

  const getStatusBadge = (file) => {
    if (file.validado === 2) return <Badge color="#009728" text="Validado" />;
    if (file.validado === 1) return <Badge color="#F8C340" text="Sin Validar" />;
    return <Badge status="error" text="Sin Registrar" />;
  };

  const stats = useMemo(() => {
    const validated = files.filter((f) => f.validado === 2).length;
    const notValidated = files.filter((f) => f.validado === 1).length;
    const notRegistered = files.filter((f) => !f.validado).length;
    return { validated, notValidated, notRegistered, total: files.length };
  }, [files]);

  const columns = [
    {
      title: "Archivo",
      dataIndex: "nameResource",
      key: "nameResource",
      width: 120,
      render: (text, file) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faFile} color="#60B5EF" />
          {text}
        </div>
      ),
    },
    {
      title: "Proyecto",
      dataIndex: "fullPath",
      key: "proyecto",
      width: 120,
      align: "center",
      render: (fullPath) => (
        <Tooltip title={getProjectName(fullPath)}>
          <span >
            {getProjectName(fullPath)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      align: "center",
      key: "estado",
      render: (_, file) => getStatusBadge(file),
    },
    {
      title: "Codigo",
      dataIndex: "codUnico",
      align: "center",
      key: "codUnico",
    },
    {
      title: "Subido por",
      dataIndex: "user",
      align: "center",
      key: "user",
    },
    {
      title: "Validado por",
      dataIndex: "validado_user",
      align: "center",
      key: "validado_user",
    },
    {
      title: "Fecha Validación",
      dataIndex: "fecha_validacion",
      align: "center",
      key: "fecha_validacion",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "center",
      render: (_, record) => (
        <Tooltip title="Visualizar el archivo">
          <Button
            onClick={() => fetchFileUrl(record.fullPath)}
            style={{ border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <FontAwesomeIcon icon={faEye} style={{ fontSize: '18px', color: '#08c' }} />
          </Button>
        </Tooltip>
      ),
    }
  ];

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="90%"
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faFolder} color="#338BC7" />
          {folderName} - {projectName}
        </div>
      }
    >
      <Row
        gutter={16}
        justify="center"
        style={{ marginBottom: 16 }}
      >
        {[
          { title: "Total", value: stats.total, color: "#338BC7" },
          { title: "Validados", value: stats.validated, color: "#009728" },
          { title: "Sin Validar", value: stats.notValidated, color: "#F8C340" },
          { title: "Sin Registrar", value: stats.notRegistered, color: "#FF4D4F" }
        ].map((item, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card>
              <Card.Meta
                title={<div style={{ textAlign: "center" }}>{item.title}</div>}
                description={
                  <div style={{
                    fontSize: 24,
                    fontWeight: "bold",
                    color: item.color,
                    textAlign: "center"
                  }}>
                    {item.value}
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
        <Input
          placeholder="Buscar archivos..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{ flex: 1, minWidth: "200px" }}
          prefix={<FontAwesomeIcon icon={faFile} style={{ color: '#ccc' }} />}
        />

        <Select
          value={statusFilter}
          onChange={handleStatusFilterChange}
          style={{ width: 180 }}
          placeholder="Filtrar por estado"
          suffixIcon={<FontAwesomeIcon icon={faFilter} />}
        >
          <Option value="all">Todos los estados</Option>
          <Option value="validated">Validados</Option>
          <Option value="notValidated">Sin Validar</Option>
          <Option value="notRegistered">Sin Registrar</Option>
        </Select>
        <ExcelExportButton
          filteredFiles={filteredFiles}
          permissionExport={permissionExport}
        />
        {(searchTerm || statusFilter !== "all") && (
          <Button
            onClick={handleClearFilters}
            type="link"
            style={{ color: '#ff4d4f' }}
          >
            Limpiar filtros
          </Button>
        )}

        <Badge
          count={`${filteredFiles.length} de ${files.length} archivos`}
          style={{
            backgroundColor: filteredFiles.length === files.length ? '#52c41a' : '#1890ff'
          }}
        />
      </div>
      {(searchTerm || statusFilter !== "all") && (
        <div style={{
          marginBottom: 16,
          padding: 8,
          backgroundColor: '#f0f8ff',
          borderRadius: 4,
          border: '1px solid #d6e4ff'
        }}>
          <Text type="secondary">
            Filtros aplicados:
            {searchTerm && ` Texto: "${searchTerm}"`}
            {searchTerm && statusFilter !== "all" && " | "}
            {statusFilter !== "all" && ` Estado: ${statusFilter === "validated" ? "Validados" :
                statusFilter === "notValidated" ? "Sin Validar" : "Sin Registrar"
              }`}
          </Text>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={paginatedFiles}
        rowKey="oid"
        pagination={false}
        scroll={{ x: 1000 }}
        locale={{
          emptyText: searchTerm || statusFilter !== "all"
            ? "No se encontraron archivos con los filtros aplicados"
            : "No hay archivos para mostrar"
        }}
      />

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, flexWrap: "wrap", gap: 8 }}>
          <div>
            Mostrando {paginatedFiles.length} archivos de {filteredFiles.length} resultados
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ fontSize: 14, color: '#666' }}>
              Página {currentPage} de {totalPages}
            </div>
            <Button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
            >
              Anterior
            </Button>
            <Button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              icon={<FontAwesomeIcon icon={faChevronRight} />}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}